import { type QueryClient, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { formatCSS, textColor } from 'colorizr';
import { useGetOrganizationsForUserQuery } from '../get-organizations-for-user/get-organizations-for-user';

export type GetOrganizationsColorMapVariables = { user_id: string };
export type GetOrganizationsColorMapQuery = Record<string, { color: string; textColor: string }>;

function getColorFromOrgId(orgId: string): string {
  const [h, s, , , l] = orgId.split('-').map((hex) => (Number.parseInt(hex, 16) % 361) / 360);
  return formatCSS({ h: h * 360, s: s * 100, l: l * 100 });
}

export const useGetOrganizationsColorMap = <TData = GetOrganizationsColorMapQuery, TError = unknown>(
  { user_id }: GetOrganizationsColorMapVariables,
  options?: Omit<UseQueryOptions<GetOrganizationsColorMapQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetOrganizationsColorMapQuery, TError, TData>({
    queryKey: ['GetOrganizationsColorMap', { user_id }],
    queryFn: async () => {
      // This is a derived query - it relies on data from useGetOrganizationsForUserQuery
      // The actual data fetching happens through the dependency, not here
      return {} as GetOrganizationsColorMapQuery;
    },
    ...options,
  });
};

useGetOrganizationsColorMap.getKey = ({ user_id }: GetOrganizationsColorMapVariables) => [
  'GetOrganizationsColorMap',
  { user_id },
];

useGetOrganizationsColorMap.fetcher = (
  { user_id }: GetOrganizationsColorMapVariables,
  queryClient: QueryClient,
): GetOrganizationsColorMapQuery => {
  const organizations = queryClient.getQueryData(useGetOrganizationsForUserQuery.getKey({ user_id }));

  if (!organizations || !Array.isArray(organizations)) {
    return {};
  }

  return organizations.reduce((acc, org) => {
    if (org.id) {
      const color = getColorFromOrgId(org.id);
      acc[org.id] = {
        color,
        textColor: textColor(color),
      };
    }
    return acc;
  }, {} as GetOrganizationsColorMapQuery);
};
